import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import { Add as AddIcon, Warning as WarningIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Vaccination {
  id: number;
  pet_id: number;
  pet_name: string;
  owner_name: string;
  vaccine_name: string;
  manufacturer: string;
  batch_number: string;
  administered_date: string;
  next_due_date: string;
  site_of_injection: string;
  administered_by_name: string;
  created_at: string;
}

interface DueSoon {
  id: number;
  pet_name: string;
  owner_name: string;
  owner_email: string;
  vaccine_name: string;
  next_due_date: string;
  days_until_due: number;
}

interface Pet {
  id: number;
  name: string;
}

const emptyForm = {
  pet_id: '',
  vaccine_name: '',
  manufacturer: '',
  batch_number: '',
  administered_date: new Date().toISOString().slice(0, 10),
  expiry_date: '',
  next_due_date: '',
  site_of_injection: '',
  reaction_notes: '',
};

const VaccinationsPage = () => {
  const [tab, setTab] = useState(0);
  const [vaccinations, setVaccinations] = useState<Vaccination[]>([]);
  const [dueSoon, setDueSoon] = useState<DueSoon[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editVax, setEditVax] = useState<Vaccination | null>(null);
  const [editForm, setEditForm] = useState({ next_due_date: '', batch_number: '', expiry_date: '', reaction_notes: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteVax, setDeleteVax] = useState<Vaccination | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vacRes, dueRes, petsRes] = await Promise.all([
        api.get<{ data: Vaccination[] }>(API_ENDPOINTS.VACCINATIONS.LIST),
        api.get<{ data: DueSoon[] }>(API_ENDPOINTS.VACCINATIONS.DUE_SOON),
        api.get<{ data: Pet[] }>(API_ENDPOINTS.PATIENTS.LIST),
      ]);
      setVaccinations(Array.isArray(vacRes.data) ? vacRes.data : []);
      setDueSoon(Array.isArray(dueRes.data) ? dueRes.data : []);
      setPets(Array.isArray(petsRes.data) ? petsRes.data : []);
    } catch (err) {
      setError('Failed to load vaccinations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.pet_id || !formData.vaccine_name || !formData.administered_date) {
      setError('Pet, vaccine name and administered date are required');
      return;
    }
    try {
      setSaving(true);
      await api.post(API_ENDPOINTS.VACCINATIONS.CREATE, {
        ...formData,
        pet_id: parseInt(formData.pet_id),
      });
      setDialogOpen(false);
      setFormData(emptyForm);
      fetchData();
    } catch (err) {
      setError('Failed to create vaccination record');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (v: Vaccination) => {
    setEditVax(v);
    setEditForm({ next_due_date: v.next_due_date || '', batch_number: v.batch_number || '', expiry_date: '', reaction_notes: '' });
  };

  const handleEdit = async () => {
    if (!editVax) return;
    setEditSaving(true);
    try {
      await api.put(API_ENDPOINTS.VACCINATIONS.UPDATE(editVax.id), editForm);
      setEditVax(null);
      fetchData();
    } catch { /* ignore */ } finally { setEditSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteVax) return;
    setDeleting(true);
    try {
      await api.delete(`/vaccinations/${deleteVax.id}`);
      setDeleteVax(null);
      fetchData();
    } catch { /* ignore */ } finally { setDeleting(false); }
  };

  const dueSoonColor = (days: number) => {
    if (days <= 7) return 'error';
    if (days <= 14) return 'warning';
    return 'info';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Vaccinations</Typography>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary">Total Records</Typography>
              <Typography variant="h4">{vaccinations.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary">Due in 30 Days</Typography>
              <Typography variant="h4" color="warning.main">{dueSoon.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary">Overdue</Typography>
              <Typography variant="h4" color="error.main">
                {dueSoon.filter(d => d.days_until_due <= 0).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Record Vaccination
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`All Records (${vaccinations.length})`} />
        <Tab label={`Due Soon (${dueSoon.length})`} icon={dueSoon.length > 0 ? <WarningIcon color="warning" /> : undefined} iconPosition="end" />
      </Tabs>

      {/* All vaccinations tab */}
      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Vaccine</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Batch #</TableCell>
                <TableCell>Date Given</TableCell>
                <TableCell>Next Due</TableCell>
                <TableCell>Given By</TableCell>
                <TableCell align="center" width={90}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vaccinations.map(v => (
                <TableRow key={v.id} hover>
                  <TableCell>{v.pet_name}</TableCell>
                  <TableCell>{v.owner_name}</TableCell>
                  <TableCell><strong>{v.vaccine_name}</strong></TableCell>
                  <TableCell>{v.manufacturer || '-'}</TableCell>
                  <TableCell>{v.batch_number || '-'}</TableCell>
                  <TableCell>{v.administered_date ? new Date(v.administered_date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    {v.next_due_date
                      ? <Chip label={new Date(v.next_due_date).toLocaleDateString()} size="small"
                          color={new Date(v.next_due_date) < new Date() ? 'error' : 'success'} />
                      : '-'}
                  </TableCell>
                  <TableCell>{v.administered_by_name || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteVax(v)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {vaccinations.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">No vaccination records found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Due soon tab */}
      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Vaccine</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Days Until Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dueSoon.map(d => (
                <TableRow key={d.id} hover>
                  <TableCell>{d.pet_name}</TableCell>
                  <TableCell>{d.owner_name}</TableCell>
                  <TableCell>{d.owner_email}</TableCell>
                  <TableCell><strong>{d.vaccine_name}</strong></TableCell>
                  <TableCell>{new Date(d.next_due_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={d.days_until_due <= 0 ? 'Overdue' : `${d.days_until_due} days`}
                      color={dueSoonColor(d.days_until_due)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {dueSoon.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">No vaccinations due in the next 30 days</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editVax} onClose={() => setEditVax(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Vaccination Record</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Next Due Date" type="date" InputLabelProps={{ shrink: true }}
            value={editForm.next_due_date} onChange={e => setEditForm(f => ({ ...f, next_due_date: e.target.value }))} />
          <TextField fullWidth margin="normal" label="Batch Number"
            value={editForm.batch_number} onChange={e => setEditForm(f => ({ ...f, batch_number: e.target.value }))} />
          <TextField fullWidth margin="normal" label="Expiry Date" type="date" InputLabelProps={{ shrink: true }}
            value={editForm.expiry_date} onChange={e => setEditForm(f => ({ ...f, expiry_date: e.target.value }))} />
          <TextField fullWidth margin="normal" label="Reaction Notes" multiline rows={2}
            value={editForm.reaction_notes} onChange={e => setEditForm(f => ({ ...f, reaction_notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditVax(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteVax} onClose={() => setDeleteVax(null)}>
        <DialogTitle>Delete Vaccination Record</DialogTitle>
        <DialogContent>
          <Typography>Delete the <strong>{deleteVax?.vaccine_name}</strong> record for <strong>{deleteVax?.pet_name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteVax(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Vaccination</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Patient *</InputLabel>
            <Select value={formData.pet_id} label="Patient *"
              onChange={e => setFormData({ ...formData, pet_id: String(e.target.value) })}>
              {pets.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Vaccine Name *" value={formData.vaccine_name}
            onChange={e => setFormData({ ...formData, vaccine_name: e.target.value })} />
          <TextField fullWidth margin="normal" label="Manufacturer" value={formData.manufacturer}
            onChange={e => setFormData({ ...formData, manufacturer: e.target.value })} />
          <TextField fullWidth margin="normal" label="Batch Number" value={formData.batch_number}
            onChange={e => setFormData({ ...formData, batch_number: e.target.value })} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth margin="normal" label="Date Administered *" type="date"
                InputLabelProps={{ shrink: true }} value={formData.administered_date}
                onChange={e => setFormData({ ...formData, administered_date: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth margin="normal" label="Vaccine Expiry Date" type="date"
                InputLabelProps={{ shrink: true }} value={formData.expiry_date}
                onChange={e => setFormData({ ...formData, expiry_date: e.target.value })} />
            </Grid>
          </Grid>
          <TextField fullWidth margin="normal" label="Next Due Date" type="date"
            InputLabelProps={{ shrink: true }} value={formData.next_due_date}
            onChange={e => setFormData({ ...formData, next_due_date: e.target.value })} />
          <TextField fullWidth margin="normal" label="Site of Injection" value={formData.site_of_injection}
            onChange={e => setFormData({ ...formData, site_of_injection: e.target.value })}
            placeholder="e.g. Left shoulder, Scruff" />
          <TextField fullWidth margin="normal" label="Reaction Notes" multiline rows={2}
            value={formData.reaction_notes}
            onChange={e => setFormData({ ...formData, reaction_notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VaccinationsPage;
